import React, { useEffect, useMemo, useState } from 'react'
import {
  CBadge,
  CButton,
  CFormInput,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { icon } from '../../shared/icons'
import { request } from '../../services'

function MonitorModal({ open, onClose }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [busyKey, setBusyKey] = useState('')
  const [response, setResponse] = useState({ open: false, loading: false, title: '', text: '', filename: 'sabre-response.txt' })

  async function load() {
    setLoading(true)
    setError('')
    const res = await request('monitor')
    setLoading(false)
    if (!res.ok) {
      setError(res.data?.error || 'No se pudo cargar el monitor')
      return
    }
    setItems((Array.isArray(res.data) ? res.data : []).sort((a, b) => String(b.updated || b.created || '').localeCompare(String(a.updated || a.created || ''))))
  }

  useEffect(() => {
    if (open) load()
  }, [open])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter((item) => [item.username, item.name, item.parent_pcc, item.pcc].join(' ').toLowerCase().includes(term))
  }, [items, search])

  const stats = {
    total: items.length,
    sabre: items.filter((item) => item.is_sabre).length,
    other: items.filter((item) => item.has_token && !item.is_sabre).length,
    empty: items.filter((item) => !item.has_token).length,
  }

  function rowKey(item) {
    return `${item.username}-${item.parent_pcc}-${item.pcc}`
  }

  async function copyToken(item) {
    setBusyKey(`copy-${rowKey(item)}`)
    try {
      let token = item.token
      if (!token || (String(token).startsWith('ATH:') && String(token).length < 80)) {
        const res = await request('get_session_token', { method: 'POST', body: { username: item.username, parent_pcc: item.parent_pcc, pcc: item.pcc } })
        token = res.ok ? res.data.token : ''
      }
      if (token) await navigator.clipboard?.writeText(token)
    } finally {
      setBusyKey('')
    }
  }

  async function viewResponse(item, kind) {
    const label = kind === 'reservation' ? 'reservationResponse' : 'remarksResponse'
    const key = `${kind}-${rowKey(item)}`
    setBusyKey(key)
    setResponse({
      open: true,
      loading: true,
      title: `${label} · ${item.username}`,
      text: '',
      filename: `${label}-${item.username || 'usuario'}-${item.pcc || 'pcc'}.txt`,
    })
    const res = await request('sabre_response', {
      method: 'POST',
      body: { kind, username: item.username, parent_pcc: item.parent_pcc, pcc: item.pcc },
    })
    setBusyKey('')
    if (!res.ok) {
      setResponse((current) => ({
        ...current,
        loading: false,
        text: `⚠ ${res.data?.error || 'No se pudo obtener respuesta SABRE'}`,
      }))
      return
    }
    setResponse((current) => ({
      ...current,
      loading: false,
      text: formatSabreResponse(res.data?.response),
    }))
  }

  function downloadResponse() {
    const blob = new Blob([response.text || ''], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = response.filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return (
    <CModal visible={open} onClose={onClose} size="xl" scrollable>
      <CModalHeader>
        <CModalTitle><CIcon icon={icon('cilMonitor')} /> Monitor de sesiones SABRE</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <div className="monitor-toolbar">
          <div className="monitor-stats">
            <span><strong>{stats.total}</strong> sesiones</span>
            <span><strong>{stats.sabre}</strong> SABRE</span>
            <span><strong>{stats.other}</strong> otros</span>
            <span><strong>{stats.empty}</strong> sin token</span>
          </div>
          <div className="monitor-actions">
            <CFormInput placeholder="Buscar sesion" value={search} onChange={(event) => setSearch(event.target.value)} />
            <CButton color="light" onClick={load}><CIcon icon={icon('cilReload')} /></CButton>
          </div>
        </div>
        {loading ? <div className="monitor-loading"><CSpinner color="success" /></div> : null}
        {error ? <div className="empty"><p>{error}</p></div> : null}
        {!loading && !error ? (
          <div className="table-responsive monitor-table-wrap">
            <CTable className="monitor-table" hover>
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell>Usuario</CTableHeaderCell>
                  <CTableHeaderCell>Nombre</CTableHeaderCell>
                  <CTableHeaderCell>Parent PCC</CTableHeaderCell>
                  <CTableHeaderCell>PCC</CTableHeaderCell>
                  <CTableHeaderCell>Token</CTableHeaderCell>
                  <CTableHeaderCell>Actualizado</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {filtered.map((item) => (
                  <CTableRow key={`${item.username}-${item.parent_pcc}-${item.pcc}`}>
                    <CTableDataCell><strong>{item.username || '-'}</strong></CTableDataCell>
                    <CTableDataCell>{item.name || '-'}</CTableDataCell>
                    <CTableDataCell><code>{item.parent_pcc || '-'}</code></CTableDataCell>
                    <CTableDataCell><code>{item.pcc || '-'}</code></CTableDataCell>
                    <CTableDataCell>
                      <div className="token-cell">
                        <CBadge color={item.is_sabre ? 'success' : item.has_token ? 'info' : 'secondary'}>
                          {item.is_sabre ? 'SABRE' : item.has_token ? 'Otro' : 'Vacio'}
                        </CBadge>
                        <code>{item.token || '-'}</code>
                        {item.has_token ? (
                          <div className="token-actions">
                            <CButton color="light" size="sm" title="Copiar token" disabled={busyKey !== ''} onClick={() => copyToken(item)}>
                              {busyKey === `copy-${rowKey(item)}` ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCopy')} />}
                            </CButton>
                            <CButton color="light" size="sm" title="Ver reservationResponse" disabled={busyKey !== ''} onClick={() => viewResponse(item, 'reservation')}>
                              {busyKey === `reservation-${rowKey(item)}` ? <CSpinner size="sm" /> : <CIcon icon={icon('cilDescription')} />}
                            </CButton>
                            <CButton color="light" size="sm" title="Ver remarksResponse" disabled={busyKey !== ''} onClick={() => viewResponse(item, 'remarks')}>
                              {busyKey === `remarks-${rowKey(item)}` ? <CSpinner size="sm" /> : <CIcon icon={icon('cilCommentBubble')} />}
                            </CButton>
                          </div>
                        ) : null}
                      </div>
                    </CTableDataCell>
                    <CTableDataCell>{item.updated || item.created || '-'}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        ) : null}
      </CModalBody>
      <CModal visible={response.open} onClose={() => setResponse({ ...response, open: false })} size="xl">
        <CModalHeader><CModalTitle>{response.title}</CModalTitle></CModalHeader>
        <CModalBody>
          {response.loading ? (
            <div className="monitor-response-loading">
              <CSpinner color="success" />
              <span>Consultando SABRE, un momento…</span>
            </div>
          ) : (
            <CFormTextarea className="json-editor monitor-response-text" rows={20} readOnly value={response.text} />
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="light" disabled={response.loading || !response.text} onClick={downloadResponse}><CIcon icon={icon('cilCloudDownload')} /> Descargar</CButton>
          <CButton color="success" className="btn-brand" onClick={() => setResponse({ ...response, open: false })}>Cerrar</CButton>
        </CModalFooter>
      </CModal>
    </CModal>
  )
}

function formatSabreResponse(raw) {
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw ?? '', null, 2)
  const trimmed = text.trim()
  if (trimmed === '') return '(respuesta vacia)'

  // Si es JSON, lo formateamos indentado.
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2)
  } catch (_) {
    // Texto plano SABRE: normalizamos saltos de linea y limpiamos espacios sobrantes.
    return trimmed
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .split('\n')
      .map((line) => line.replace(/\s+$/g, ''))
      .join('\n')
  }
}

export default MonitorModal
